import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";

const STATS = [
  { num: 100, suffix: "+", label: "Artists, bands & events worked with" },
  { num: 50, suffix: "+", label: "Live events curated, marketed & produced" },
  { num: 20, suffix: "+", label: "Years inside the Indian music scene" },
  { num: 2024, from: 1990, suffix: "", label: "Founded by working musicians" },
];

const CLIENTS = ["GUTSLIT", "MOTHERJANE", "ZYGNEMA", "SPACE IS ALL WE HAVE"]; // eslint-disable-line no-unused-vars -- kept for future use per client

const SERVICES = [
  "Film Production",
  "Audio Production",
  "Social Media Content",
  "Growth Strategies",
  "Marketing Systems",
  "Consistent Social Media Ops",
  "Performance Ads",
  "PR & Press",
  "Outreach",
  "Brand Strategy",
  "Event Marketing",
  "Release Campaigns",
];

const Counter = ({ num, suffix, from = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const [display, setDisplay] = useState(from);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(from, num, {
      duration: 1.6,
      ease: [0.2, 0.8, 0.2, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, num, from]);

  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
};

const MarqueeRow = ({ items, reverse = false }) => (
  <div className="overflow-hidden py-2.5">
    <div className={`marquee-track ${reverse ? "marquee-reverse" : ""}`}>
      {[...items, ...items, ...items].map((name, i) => (
        <span key={`${name}-${i}`} className="flex items-center shrink-0">
          <span
            className={`font-display uppercase whitespace-nowrap px-5 sm:px-8 ${
              reverse ? "text-xl sm:text-2xl text-white/45" : "text-2xl sm:text-3xl text-white/85"
            }`}
          >
            {name}
          </span>
          <span className="h-2 w-2 rounded-full bg-[color:var(--hs-lime)] shrink-0" aria-hidden="true" />
        </span>
      ))}
    </div>
  </div>
);

export const ProofBand = () => {
  return (
    <section className="relative py-14 sm:py-20" data-testid="proof-band">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="proof-stats">
          {STATS.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 36, rotateX: 12 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.6, delay: i * 0.09, ease: [0.2, 0.8, 0.2, 1] }}
              className="rounded-2xl border border-white/10 bg-black/30 p-5 sm:p-7"
            >
              <div className="font-display text-4xl sm:text-5xl text-[color:var(--hs-lime)]">
                <Counter num={stat.num} suffix={stat.suffix} from={stat.from || 0} />
              </div>
              <div className="mt-2 text-xs sm:text-sm text-[color:var(--hs-ink-dim)] leading-snug">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Services marquee — what we actually do, at full volume */}
      <div className="mt-12 sm:mt-14 -rotate-2 scale-[1.02]" data-testid="services-marquee">
        <div className="border-y border-[color:rgba(202,255,26,0.25)] bg-[color:rgba(202,255,26,0.05)]">
          <MarqueeRow items={SERVICES} />
          <div className="border-t border-white/10">
            <MarqueeRow items={[...SERVICES].reverse()} reverse />
          </div>
        </div>
      </div>
      <p className="text-center mt-8 font-mono-ui text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[color:var(--hs-ink-dim)]">
        One stop shop · Content to campaigns to growth
      </p>
    </section>
  );
};
